import React from 'react'
import DealModalSection from './DealModalSection';
import { useText } from '../context/TextContext';
import SearchInput from './SearchInput';
import { search } from '../utils/api';
import { useUser } from '../context/UserContext';
import { useOrganization } from '../context/OrganizationContext';
import { COLLECTIONS, PATHS, QUERY_PARAMS } from '../utils/shared/constants';
import CustomerName from './CustomerName';
import { useFirebaseDoc } from '../hooks/firebaseHooks';
import { Customer } from '../models/shared/Customer';
import { AdvancedButton } from './AdvancedButton';
import CustomerContact from './CustomerContact';
import { Button, ButtonGroup, DropdownButton } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { AdvancedDropdownItem } from './AdvancedDropdownItem';

interface Props {
    dealId: string;
    currentCustomerId?: string;
    onCustomerSelect: (customerId: string) => Promise<void>;
    className?: string;
    readOnly?: boolean;
}

export default function CustomerSearch(props: Props) {
    const text = useText();
    const user = useUser();
    const { orgId } = useOrganization();
    const [forceSearch, setForceSearch] = React.useState(false);
    const [customer] = useFirebaseDoc<Customer>(`${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.CUSTOMERS}/${props.currentCustomerId}`);
    const [query, setQuery] = React.useState('');
    const navigate = useNavigate();

    const searchCustomer = async (query: string) => {
        const userToken = await user.getToken();
        const searchResult = await search(userToken, query, orgId, { type: COLLECTIONS.CUSTOMERS });
        return searchResult.hits.customers;
    }

    if (props.currentCustomerId && !forceSearch) {
        return <DealModalSection title={text.customer} open sizeDependencies={[customer]}>
            <div className='d-flex justify-content-between'>

                <CustomerName customer={customer} />

                {!props.readOnly && <ButtonGroup>
                    <AdvancedButton size="sm" onClick={() => navigate(`${PATHS.CUSTOMERS_EDIT}/${props.currentCustomerId}`)} variant='outline-secondary'
                        icon='bi-pencil' />
                    <DropdownButton size='sm' variant='outline-secondary' drop='down-centered' as={ButtonGroup} title="">
                        <AdvancedDropdownItem icon='bi-recycle' text={text.changeCustomer} onClick={() => setForceSearch(true)} />

                    </DropdownButton>
                </ButtonGroup>
                }
            </div>
            <CustomerContact customer={customer} />
        </DealModalSection>
    }

    const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const params = new URLSearchParams({
            [QUERY_PARAMS.DEAL_ID]: props.dealId,
            [QUERY_PARAMS.QUERY]: query,
        });
        navigate(`${PATHS.CUSTOMERS_EDIT}?${params.toString()}`);
    }

    if (props.readOnly) {
        return null;
    }

    return (
        <DealModalSection title={text.customer} open  >

            <form onSubmit={onSubmit}>

                <SearchInput
                    searchCallback={searchCustomer}
                    onChange={setQuery}
                    renderCandidate={(candidate) => {
                        return <CustomerName customer={candidate} />
                    }}
                    handleSelect={(candidate) => {
                        setForceSearch(false);
                        return props.onCustomerSelect(candidate.objectID)
                    }}
                    placeHolder={text.searchCustomer}
                    onCancel={() => setForceSearch(false)}
                    cancelable={forceSearch}
                    renderEmpty={() => <div className='px-3 py-2'>
                        <div className='text-muted'>{text.noResults}</div>
                        <Button type='submit' className='p-0' size='sm' variant='text'
                            dangerouslySetInnerHTML={{ __html: text.pressEnterToCreateNewCustomer }}>
                        </Button>
                    </div>}

                    extraAction={
                        <AdvancedButton
                            variant='outline-primary'
                            size='sm'
                            icon={'bi-plus'}
                            type='submit'
                            title={text.newCustomer}
                        />
                    }
                />
            </form>
        </DealModalSection>
    )
}
